// DEBT-VITA™ — Comparateur multi-pays / multi-années
// Permet de comparer 2 projets sauvegardés OU une référence externe (FMI/OCDE)
// Affiche tableau comparatif + barchart Recharts.
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Globe2, Play, RefreshCw, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { listDebtProjectsSummary, DEBT_COLORS } from "@/lib/debt";

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------
function fmt(v, digits = 1) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return Number(v).toLocaleString("fr-FR", {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
}
function fmtMoney(v, currency = "UM") {
  if (v === null || v === undefined) return "—";
  return `${fmt(v, 0)} ${currency}`;
}
function diffColor(a, b, lowerIsBetter = true) {
  if (a === null || a === undefined || b === null || b === undefined) return "text-muted-foreground";
  if (a === b) return "text-muted-foreground";
  const aBetter = lowerIsBetter ? a < b : a > b;
  return aBetter
    ? "text-emerald-700 dark:text-emerald-400"
    : "text-red-700 dark:text-red-400";
}
function fmtDelta(a, b, suffix = "", digits = 1) {
  if (a === null || a === undefined || b === null || b === undefined) return "—";
  const d = a - b;
  const sign = d > 0 ? "+" : "";
  return `${sign}${fmt(d, digits)}${suffix}`;
}

const ALERT_LABELS = {
  1: "SOUTENABLE",
  2: "SOUS TENSION",
  3: "CRITIQUE",
  4: "INSOUTENABLE",
};

function deriveAlertFromPctCarriere(pct) {
  if (pct === null || pct === undefined) return null;
  if (pct < 10) return 1;
  if (pct < 25) return 2;
  if (pct < 50) return 3;
  return 4;
}

// ----------------------------------------------------------------------------
// External reference form
// ----------------------------------------------------------------------------
function ExternalReferenceForm({ value, onChange }) {
  return (
    <div className="space-y-2 rounded-lg border border-dashed border-amber-400/50 bg-amber-50/50 p-3 dark:bg-amber-950/20">
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-amber-800 dark:text-amber-300">
        <Globe2 className="h-3.5 w-3.5" />
        Référence externe (FMI / OCDE)
      </div>
      <div>
        <Label className="text-[11px] text-muted-foreground">Pays / entité</Label>
        <Input
          type="text"
          value={value.country || ""}
          onChange={(e) => onChange({ ...value, country: e.target.value })}
          placeholder="Ex: France, Allemagne, OCDE moyenne"
          className="mt-1 h-9"
          data-testid="ext-ref-country"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px] text-muted-foreground">Dette/PIB (%)</Label>
          <Input
            type="number"
            step="0.1"
            value={value.debt_to_gdp_ratio ?? ""}
            onChange={(e) =>
              onChange({ ...value, debt_to_gdp_ratio: e.target.value })
            }
            className="mt-1 h-9"
            data-testid="ext-ref-debt-gdp"
          />
        </div>
        <div>
          <Label className="text-[11px] text-muted-foreground">Service/recettes (%)</Label>
          <Input
            type="number"
            step="0.1"
            value={value.service_to_revenue_ratio ?? ""}
            onChange={(e) =>
              onChange({ ...value, service_to_revenue_ratio: e.target.value })
            }
            className="mt-1 h-9"
            data-testid="ext-ref-service-rev"
          />
        </div>
      </div>
      <p className="text-[10px] italic text-muted-foreground">
        Les références externes ne fournissent que 2 ratios — la comparaison ne
        portera que sur ces dimensions et le niveau d'alerte sera estimé.
      </p>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Side selector (project picker OR external ref)
// ----------------------------------------------------------------------------
function SideSelector({ side, projects, value, onChange }) {
  const sideLabel = side === "A" ? "Pays / Année 1" : "Pays / Année 2";
  return (
    <Card className="overflow-hidden" data-testid={`debt-cmp-side-${side}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{sideLabel}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label className="text-[11px] text-muted-foreground">
            Mode de sélection
          </Label>
          <Select
            value={value.mode}
            onValueChange={(v) => onChange({ ...value, mode: v })}
          >
            <SelectTrigger
              className="mt-1 h-9"
              data-testid={`debt-cmp-${side}-mode`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="project">Projet sauvegardé</SelectItem>
              <SelectItem value="external">Référence externe</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {value.mode === "project" ? (
          <div>
            <Label className="text-[11px] text-muted-foreground">
              Choisir un projet
            </Label>
            <Select
              value={value.projectId || ""}
              onValueChange={(v) => onChange({ ...value, projectId: v })}
              disabled={projects.length === 0}
            >
              <SelectTrigger
                className="mt-1 h-9"
                data-testid={`debt-cmp-${side}-project`}
              >
                <SelectValue placeholder={projects.length === 0 ? "Aucun projet calculé" : "Sélectionner..."} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.project_id} value={p.project_id}>
                    {p.project_name}
                    {p.country ? ` — ${p.country}` : ""}
                    {p.fiscal_year ? ` (${p.fiscal_year})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <ExternalReferenceForm
            value={value.external || {}}
            onChange={(ext) => onChange({ ...value, external: ext })}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Build comparable model from a side state + projects list
// ----------------------------------------------------------------------------
function buildSideModel(side, projects) {
  if (side.mode === "project" && side.projectId) {
    const p = projects.find((x) => x.project_id === side.projectId);
    if (!p) return null;
    return {
      label:
        (p.country || p.project_name) +
        (p.fiscal_year ? ` (${p.fiscal_year})` : ""),
      currency: p.currency || "UM",
      isExternal: false,
      debt_to_gdp_ratio: p.debt_to_gdp_ratio,
      annual_debt_service: p.annual_debt_service,
      annual_fiscal_deficit: p.annual_fiscal_deficit,
      results: p.results || {},
      alertLevel: deriveAlertFromPctCarriere(p.results?.pct_carriere_dette),
    };
  }
  if (side.mode === "external") {
    const ext = side.external || {};
    if (!ext.country) return null;
    const ratio = parseFloat(ext.debt_to_gdp_ratio);
    return {
      label: ext.country + " (référence externe)",
      currency: "—",
      isExternal: true,
      debt_to_gdp_ratio: Number.isFinite(ratio) ? ratio : null,
      annual_debt_service: null,
      annual_fiscal_deficit: null,
      results: {},
      alertLevel:
        Number.isFinite(ratio)
          ? ratio < 60
            ? 1
            : ratio < 90
            ? 2
            : ratio < 130
            ? 3
            : 4
          : null,
      externalDetails: {
        debt_to_gdp_ratio: Number.isFinite(ratio) ? ratio : null,
        service_to_revenue_ratio: parseFloat(ext.service_to_revenue_ratio) || null,
      },
    };
  }
  return null;
}

// ----------------------------------------------------------------------------
// Comparator component
// ----------------------------------------------------------------------------
export default function DebtComparator() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sideA, setSideA] = useState({ mode: "project", projectId: null, external: {} });
  const [sideB, setSideB] = useState({ mode: "project", projectId: null, external: {} });
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const list = await listDebtProjectsSummary();
        if (cancelled) return;
        setProjects(list);
        if (list.length > 0) {
          setSideA((s) => ({ ...s, projectId: list[0].project_id }));
        }
        if (list.length > 1) {
          setSideB((s) => ({ ...s, projectId: list[1].project_id }));
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        toast.error("Impossible de charger les projets pour la comparaison.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const modelA = useMemo(() => buildSideModel(sideA, projects), [sideA, projects]);
  const modelB = useMemo(() => buildSideModel(sideB, projects), [sideB, projects]);

  const canCompare = modelA && modelB;

  function runCompare() {
    if (!canCompare) {
      toast.error("Renseignez les deux côtés du comparateur.");
      return;
    }
    setShowResults(true);
  }

  function reset() {
    setShowResults(false);
  }

  // Build chart dataset (5 indicateurs)
  const chartData = useMemo(() => {
    if (!modelA || !modelB) return [];
    const ra = modelA.results || {};
    const rb = modelB.results || {};
    return [
      {
        label: "Dette / tête (UM)",
        A: ra.dette_par_tete_um ?? 0,
        B: rb.dette_par_tete_um ?? 0,
      },
      {
        label: "Jours vie hypothéqués",
        A: ra.jours_vie_dette ?? 0,
        B: rb.jours_vie_dette ?? 0,
      },
      {
        label: "Années vie",
        A: ra.annees_vie_dette ?? 0,
        B: rb.annees_vie_dette ?? 0,
      },
      {
        label: "% carrière",
        A: ra.pct_carriere_dette ?? 0,
        B: rb.pct_carriere_dette ?? 0,
      },
      {
        label: "Service j/an",
        A: ra.jours_vie_service_annuel ?? 0,
        B: rb.jours_vie_service_annuel ?? 0,
      },
    ];
  }, [modelA, modelB]);

  if (loading) {
    return (
      <Card data-testid="debt-comparator-loading">
        <CardContent className="p-6 text-sm text-muted-foreground">
          Chargement du comparateur…
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="debt-comparator">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Comparateur dette multi-pays / multi-années
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            {projects.length === 0
              ? "Aucun projet n'a encore été calculé. Lancez un calcul DEBT-VITA™ sur au moins un projet pour pouvoir comparer."
              : `${projects.length} projet(s) disponibles. Choisissez deux références (projet ou donnée externe).`}
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <SideSelector
              side="A"
              projects={projects}
              value={sideA}
              onChange={setSideA}
            />
            <SideSelector
              side="B"
              projects={projects}
              value={sideB}
              onChange={setSideB}
            />
          </div>

          <div className="mt-4 flex items-center gap-3">
            <Button
              onClick={runCompare}
              disabled={!canCompare}
              className="bg-red-700 text-white hover:bg-red-800"
              data-testid="debt-cmp-run"
            >
              <Play className="mr-2 h-4 w-4" />
              Comparer
            </Button>
            {showResults ? (
              <Button
                variant="outline"
                onClick={reset}
                data-testid="debt-cmp-reset"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Réinitialiser
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {showResults && modelA && modelB ? (
        <ComparisonResults modelA={modelA} modelB={modelB} chartData={chartData} />
      ) : null}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Comparison results
// ----------------------------------------------------------------------------
function Row({ label, A, B, suffix = "", digits = 1, lowerIsBetter = true, formatter, testId }) {
  const fA = formatter ? formatter(A) : fmt(A, digits) + suffix;
  const fB = formatter ? formatter(B) : fmt(B, digits) + suffix;
  return (
    <tr data-testid={testId}>
      <td className="border-b border-border px-3 py-2 text-sm font-medium">{label}</td>
      <td className="border-b border-border px-3 py-2 text-right font-mono text-sm">
        {fA}
      </td>
      <td className="border-b border-border px-3 py-2 text-right font-mono text-sm">
        {fB}
      </td>
      <td
        className={
          "border-b border-border px-3 py-2 text-right font-mono text-sm font-bold " +
          diffColor(A, B, lowerIsBetter)
        }
      >
        {fmtDelta(A, B, suffix, digits)}
      </td>
    </tr>
  );
}

function ComparisonResults({ modelA, modelB, chartData }) {
  const ra = modelA.results || {};
  const rb = modelB.results || {};
  return (
    <Card data-testid="debt-cmp-results">
      <CardHeader>
        <CardTitle className="text-base">
          Tableau comparatif
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Vert : <strong>{modelA.label}</strong> est moins endetté en vies ;
          Rouge : plus endetté.
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full table-fixed border-collapse text-left">
            <thead>
              <tr className="border-b-2 border-border bg-muted/30">
                <th className="px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                  Indicateur
                </th>
                <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-widest">
                  {modelA.label}
                </th>
                <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-widest">
                  {modelB.label}
                </th>
                <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                  Écart
                </th>
              </tr>
            </thead>
            <tbody>
              <Row
                testId="cmp-row-dpc"
                label="Dette par contribuable"
                A={ra.dette_par_tete_um}
                B={rb.dette_par_tete_um}
                formatter={(v) =>
                  v === null || v === undefined
                    ? modelA.isExternal || modelB.isExternal
                      ? "n/a"
                      : "—"
                    : fmtMoney(v, "UM")
                }
                digits={0}
              />
              <Row
                testId="cmp-row-d2"
                label="Jours de vie hypothéqués"
                A={ra.jours_vie_dette}
                B={rb.jours_vie_dette}
                suffix=" j"
                digits={1}
              />
              <Row
                testId="cmp-row-d3"
                label="Années vie dette"
                A={ra.annees_vie_dette}
                B={rb.annees_vie_dette}
                suffix=" ans"
                digits={3}
              />
              <Row
                testId="cmp-row-d4"
                label="% carrière sacrifiée"
                A={ra.pct_carriere_dette}
                B={rb.pct_carriere_dette}
                suffix=" %"
                digits={2}
              />
              <Row
                testId="cmp-row-d5"
                label="Jours/an pour rembourser"
                A={ra.jours_vie_service_annuel}
                B={rb.jours_vie_service_annuel}
                suffix=" j/an"
                digits={1}
              />
              <Row
                testId="cmp-row-tavd"
                label="TAVD (%)"
                A={ra.tavd}
                B={rb.tavd}
                suffix=" %"
                digits={1}
              />
              <Row
                testId="cmp-row-debt-gdp"
                label="Dette / PIB (%)"
                A={modelA.debt_to_gdp_ratio}
                B={modelB.debt_to_gdp_ratio}
                suffix=" %"
                digits={2}
              />
              <tr data-testid="cmp-row-alert">
                <td className="border-b border-border px-3 py-2 text-sm font-medium">
                  Niveau d'alerte vitaliste
                </td>
                <td className="border-b border-border px-3 py-2 text-right font-mono text-sm">
                  {ALERT_LABELS[modelA.alertLevel] || "—"}
                </td>
                <td className="border-b border-border px-3 py-2 text-right font-mono text-sm">
                  {ALERT_LABELS[modelB.alertLevel] || "—"}
                </td>
                <td className="border-b border-border px-3 py-2 text-right text-xs text-muted-foreground">
                  ─
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-6">
          <h4 className="mb-2 text-sm font-semibold">
            Poids vitaliste comparé de la dette publique
          </h4>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} margin={{ left: 4, right: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#aaa" strokeOpacity={0.2} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip
                formatter={(v) =>
                  Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 2 })
                }
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar
                dataKey="A"
                name={modelA.label}
                fill={DEBT_COLORS.blue}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="B"
                name={modelB.label}
                fill={DEBT_COLORS.red}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {(modelA.isExternal || modelB.isExternal) ? (
          <p className="mt-3 rounded-md border border-amber-300/40 bg-amber-50/50 p-2 text-[11px] text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
            ⓘ Une référence externe a été utilisée. Les indicateurs vitalistes
            (jours de vie, années) ne sont pas comparables si la référence ne
            fournit pas un calcul vitaliste complet — seuls les ratios
            macro (Dette/PIB, TAVD si calculé) sont fiables.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
